import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";

import { Undo as UndoIcon, Warning as WarningIcon } from "@material-ui/icons";

import { useState, useEffect, useMemo } from "react";

// RTK Query
import { useBulkRevertirMutation } from "src/services/conciliacionApi";

// Helpers
import {
  formatearMonto,
  formatearRutDisplay,
} from "src/utils/conciliacionHelpers";

// Snackbar
import { useSnackbar } from "src/components/snackbar";

export default function DialogRevertirBulk({
  open,
  onClose,
  movimientos = [],
}) {
  // ==================== ESTADOS ====================
  const [motivo, setMotivo] = useState("");

  // ==================== HOOKS ====================
  const [bulkRevertir, { isLoading: isRevirtiendo }] =
    useBulkRevertirMutation();
  const { enqueueSnackbar } = useSnackbar();

  // ==================== EFECTOS ====================

  // Limpiar motivo al abrir/cerrar el diálogo
  useEffect(() => {
    if (open) {
      setMotivo("");
    }
  }, [open]);

  // ==================== CÁLCULOS ====================

  // Extraer conciliaciones válidas
  const conciliacionesValidas = useMemo(() => {
    return movimientos
      .filter(
        (mov) =>
          mov?.conciliacion?.id && mov.conciliacion.estado !== "REVERTIDO",
      )
      .map((mov) => ({
        conciliacion_id: mov.conciliacion.id,
        movimiento_id: mov.id,
        monto: mov.monto,
        rut: mov.rut_ordenante,
        nombre: mov.nombre_ordenante,
        fecha: mov.fecha_mov,
        num_facturas: mov.conciliacion?.detalles?.length || 0,
      }));
  }, [movimientos]);

  const totalConciliaciones = conciliacionesValidas.length;
  const totalMonto = conciliacionesValidas.reduce(
    (sum, c) => sum + parseFloat(c.monto || 0),
    0,
  );

  // ==================== HELPERS ====================

  const truncarTexto = (texto, maxLength = 40) => {
    if (!texto) return "-";
    return texto.length > maxLength
      ? `${texto.substring(0, maxLength)}...`
      : texto;
  };

  // ==================== HANDLERS ====================

  const handleClose = () => {
    if (!isRevirtiendo) {
      setMotivo("");
      onClose();
    }
  };

  const handleMotivoChange = (event) => {
    setMotivo(event.target.value);
  };

  const handleConfirmarRevertir = async () => {
    if (conciliacionesValidas.length === 0) {
      enqueueSnackbar("No hay conciliaciones válidas para revertir", {
        variant: "warning",
      });
      return;
    }

    try {
      const resultado = await bulkRevertir({
        conciliacion_ids: conciliacionesValidas.map((c) => c.conciliacion_id),
        motivo: motivo || "Reversión masiva sin motivo especificado",
      }).unwrap();

      // Procesar resultados
      const exitosos = resultado.resultados?.exitosos?.length || 0;
      const fallidos = resultado.resultados?.fallidos?.length || 0;

      if (fallidos === 0) {
        // Todos exitosos
        enqueueSnackbar(
          `${exitosos} conciliación${exitosos > 1 ? "es" : ""} revertida${
            exitosos > 1 ? "s" : ""
          } exitosamente`,
          { variant: "success" },
        );
      } else if (exitosos === 0) {
        // Todos fallidos
        enqueueSnackbar(`Error: No se pudo revertir ninguna conciliación`, {
          variant: "error",
        });
      } else {
        // Resultados mixtos
        enqueueSnackbar(
          `${exitosos} exitosa${exitosos > 1 ? "s" : ""}, ${fallidos} fallida${
            fallidos > 1 ? "s" : ""
          }`,
          { variant: "warning" },
        );
      }

      // Limpiar y cerrar
      setMotivo("");
      onClose(resultado);
    } catch (error) {
      console.error("Error al revertir conciliaciones:", error);
      enqueueSnackbar(
        error?.data?.mensaje ||
          error?.data?.message ||
          "Error al revertir las conciliaciones",
        { variant: "error" },
      );
    }
  };

  // ==================== VALIDACIONES ====================

  if (!open) return null;

  if (totalConciliaciones === 0) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WarningIcon style={{ color: "#ff9800", marginRight: 8 }} />
            <Typography variant="h6">
              No hay conciliaciones para revertir
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Los movimientos seleccionados no tienen conciliaciones asociadas o
            ya fueron revertidos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ==================== RENDER ====================

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <UndoIcon style={{ color: "#f44336", marginRight: 8 }} />
          <Typography variant="h6" component="div" style={{ color: "#f44336" }}>
            ¿Confirmar Reversión Masiva?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* ADVERTENCIA */}
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Esta acción revertirá {totalConciliaciones} conciliación
            {totalConciliaciones > 1 ? "es" : ""}
          </Typography>
          <Typography variant="caption">
            Los movimientos volverán a estado PENDIENTE y las facturas quedarán
            disponibles para nueva conciliación.
          </Typography>
        </Alert>

        {/* RESUMEN */}
        <Card
          variant="outlined"
          style={{ backgroundColor: "#FFF3E0", marginBottom: 16 }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              RESUMEN DE REVERSIÓN
            </Typography>
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography variant="body2">
                <strong>Total conciliaciones:</strong> {totalConciliaciones}
              </Typography>
              <Typography variant="body2">
                <strong>Monto total:</strong> {formatearMonto(totalMonto)}
              </Typography>
            </Box>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              <strong>Total facturas afectadas:</strong>{" "}
              {conciliacionesValidas.reduce(
                (sum, c) => sum + c.num_facturas,
                0,
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* LISTA DE CONCILIACIONES (máximo 5 visibles) */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            CONCILIACIONES A REVERTIR:
          </Typography>
          <List dense style={{ maxHeight: 200, overflow: "auto" }}>
            {conciliacionesValidas.slice(0, 10).map((conc, index) => (
              <ListItem key={index} divider>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{formatearMonto(conc.monto)}</strong> -{" "}
                      {truncarTexto(conc.nombre, 35)}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      {formatearRutDisplay(conc.rut)} • {conc.num_facturas}{" "}
                      factura{conc.num_facturas !== 1 ? "s" : ""}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
          {conciliacionesValidas.length > 10 && (
            <Typography
              variant="caption"
              color="textSecondary"
              align="center"
              display="block"
            >
              ... y {conciliacionesValidas.length - 10} más
            </Typography>
          )}
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        {/* CAMPO DE MOTIVO */}
        <TextField
          autoFocus
          margin="dense"
          label="Motivo de reversión (opcional)"
          fullWidth
          multiline
          rows={3}
          value={motivo}
          onChange={handleMotivoChange}
          placeholder="Ej: Corrección de error en conciliaciones del lote..."
          variant="outlined"
          helperText="Este motivo quedará registrado en la auditoría de todas las reversiones"
          disabled={isRevirtiendo}
        />
      </DialogContent>

      {/* ACCIONES */}
      <DialogActions>
        <Button onClick={handleClose} disabled={isRevirtiendo}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmarRevertir}
          color="secondary"
          variant="contained"
          disabled={isRevirtiendo || totalConciliaciones === 0}
          startIcon={
            isRevirtiendo ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <UndoIcon />
            )
          }
        >
          {isRevirtiendo
            ? "Revirtiendo..."
            : `Revertir ${totalConciliaciones} conciliación${
                totalConciliaciones > 1 ? "es" : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
